import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './StandardPopup.module.scss';
import { makeStyles } from '@material-ui/core';
import close from '../../assets/svg/close-dark.svg';
import infoIcon from '../../assets/svg/info.svg';
import { ToastType } from 'constants/index';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import { ReactComponent as WarningIcon } from '../../assets/svg/warning.svg';
import { ComponentType, ReactNode } from 'react';
import themes from 'styles/themes';
import { Spinner } from 'react-bootstrap';

interface PopupProps {
    show: boolean;
    onHide: Function;
    title: ReactNode;
    content: ReactNode;
    customFooter?: ReactNode;
    button1: string;
    button2: string;
    type: ToastType;
    closeButton: boolean;
    // onEvent?: Function,
    buttonCancel?: string;
    onFirstButtonClick?: Function;
    onCancelClick?: Function;
    onSecondButtonClick?: Function;
    Icon?: ComponentType;
    dialogContentClassName?: string;
    fontSize?: string;
    buttonFontSize?: number;
    disableButton1?: boolean;
    disableButton2?: boolean;
    loadingButton1?: boolean;
    loadingButton2?: boolean;
}

interface StyleProps {
    fontSize?: string;
    buttonFontSize?: number;
    isLargeButton?: boolean;
}

const useStyles = makeStyles(() => ({
    icon: {
        color: 'orange',
        width: '42px',
        marginBottom: '15px',
        marginTop: '20px',
        margin: '0 auto',
    },
    title: (props: StyleProps) => ({
        width: '100%',
        textAlign: 'center',
        color: themes.palette.primary.main,
        fontSize: props?.fontSize === 'small' ? '16px' : '18px',
        padding: '0 10px ',
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        fontFamily: 'Fraunces',
    }),
    closeButton: {
        border: 'none',
        background: 'transparent',
        position: 'absolute',
        top: '10px',
        right: '10px',
    },
    content: (props: StyleProps) => ({
        textAlign: 'center',
        padding: '5px',
        color: themes.palette.primary.main,
        fontFamily: 'PoppinsRegular',
        fontSize: props?.fontSize === 'small' ? '14px' : '16px',
        maxWidth: '100%',
    }),
    buttonGroup: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        paddingBottom: 10,
    },
    button1: (props: StyleProps) => ({
        backgroundColor: '#fff',
        border: `1px solid ${themes.palette.primary.main}`,
        borderRadius: '25px',
        color: themes.palette.primary.main,
        padding: '9px',
        fontWeight: 500,
        fontSize: props?.buttonFontSize || 12,
        minWidth: props?.isLargeButton ? 200 : 127,
        width: 'auto',
        margin: 2,
        fontFamily: 'PoppinsRegular',
        textTransform: 'uppercase',
        marginBottom: 10,
    }),
    button2: (props: StyleProps) => ({
        width: 'fit-content',
        minWidth: props?.isLargeButton ? 200 : 127,
        backgroundColor: themes.palette.primary.main,
        borderRadius: '25px',
        color: '#fff',
        padding: '9px',
        fontWeight: 500,
        fontSize: props?.buttonFontSize || 12,
        border: `1px solid ${themes.palette.primary.main}`,
        margin: 2,
        fontFamily: 'PoppinsRegular',
        textTransform: 'uppercase',
        marginBottom: 10,
    }),
}));

const StandardPopup = ({
    show,
    onHide,
    title,
    content,
    button1,
    button2,
    type,
    closeButton,
    buttonCancel,
    onFirstButtonClick,
    onCancelClick,
    onSecondButtonClick,
    Icon,
    dialogContentClassName,
    fontSize,
    buttonFontSize,
    customFooter,
    disableButton1,
    disableButton2,
    loadingButton1,
    loadingButton2,
}: Partial<PopupProps>) => {
    const isLargeButton = (button1 ? button1?.length > 13 : false) || (button2 ? button2?.length > 13 : false);

    const classes = useStyles({ fontSize, buttonFontSize, isLargeButton });
    return (
        <Modal
            className={styles['modal-dialog']}
            show={show}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            dialogClassName={dialogContentClassName ? dialogContentClassName : ''}
            contentClassName={styles['modal-content']}
        >
            {closeButton ? (
                <button
                    onClick={() => {
                        if (onHide) {
                            onHide();
                        }
                    }}
                    className={classes.closeButton}
                >
                    <img src={close} alt="" />
                </button>
            ) : (
                ''
            )}
            {Icon ? (
                <Icon />
            ) : type === ToastType.Info ? (
                <img src={infoIcon} alt="" className={classes.icon} />
            ) : type === ToastType.Success ? (
                <span>
                    <CheckCircleOutlineIcon
                        style={{ fontSize: 50, margin: '15px 0', color: themes.palette.primary.main }}
                    />
                </span>
            ) : type === ToastType.Error ? (
                <WarningIcon className={classes.icon} style={{ height: 42 }} />
            ) : type === ToastType.Warning ? (
                <span>
                    <HelpOutlineOutlinedIcon style={{ fontSize: 50, margin: '15px 0', color: 'orange' }} />
                </span>
            ) : (
                ''
            )}
            <h5 className={classes.title}>{title}</h5>
            <div
                className={classes.content}
                style={{ marginBottom: customFooter || (!button1 && !button2 && !buttonCancel) ? 0 : '1rem' }}
            >
                {content}
            </div>
            {customFooter ? (
                customFooter
            ) : button1 || button2 || buttonCancel ? (
                <div className={classes.buttonGroup}>
                    {button1 && (
                        <button
                            className={classes.button1}
                            onClick={() => {
                                // Check the condition where item might not be available - both buttons will be visible
                                if (onFirstButtonClick) {
                                    onFirstButtonClick();
                                }
                                // if (onEvent) onEvent();
                                if (onHide) {
                                    onHide();
                                }
                            }}
                            disabled={typeof disableButton1 === 'boolean' ? disableButton1 : false}
                        >
                            {button1}
                            {loadingButton1 && (
                                <Spinner animation="border" style={{ marginLeft: 8, width: 15, height: 15 }} />
                            )}
                        </button>
                    )}
                    {buttonCancel && (
                        <button
                            className={classes.button1}
                            onClick={() => {
                                if (onHide) {
                                    onHide();
                                }
                                if (onCancelClick) {
                                    onCancelClick();
                                }
                            }}
                        >
                            {buttonCancel}
                        </button>
                    )}
                    {button2 && (
                        <button
                            className={classes.button2}
                            onClick={() => {
                                if (onSecondButtonClick) onSecondButtonClick();
                            }}
                            disabled={typeof disableButton2 === 'boolean' ? disableButton1 : false}
                        >
                            {button2}
                            {loadingButton2 && (
                                <Spinner animation="border" style={{ marginLeft: 8, width: 15, height: 15 }} />
                            )}
                        </button>
                    )}
                </div>
            ) : (
                <></>
            )}
        </Modal>
    );
};

export default StandardPopup;
